import { Modal as AntdModal } from 'antd';
import type { ReactNode } from 'react';

import type { ButtonVariant } from '@/components/common/button';

import { OverlayChrome, OverlayFooter } from '../overlay';

import styles from './modal.module.scss';

export type ModalSize = 'sm' | 'md' | 'lg';

const SIZE_WIDTH: Record<ModalSize, number> = { sm: 464, md: 560, lg: 709 };

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  size?: ModalSize;
  width?: number | string;
  maskClosable?: boolean;
  closeOnEsc?: boolean;
  loading?: boolean;
  children: ReactNode;
  primaryText?: string;
  onPrimary?: () => void;
  primaryVariant?: ButtonVariant;
  primaryDanger?: boolean;
  cancelText?: string;
  onCancel?: () => void;
  /** Internal: used by FormModal to wrap body + footer in a <form>. */
  onSubmit?: React.ComponentProps<'form'>['onSubmit'];
  /** Internal: makes the primary footer button type="submit". */
  primaryIsSubmit?: boolean;
  footerAlign?: 'end' | 'center';
  hideFooter?: boolean;
};

export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  width,
  maskClosable = true,
  closeOnEsc = true,
  loading = false,
  children,
  primaryText,
  onPrimary,
  primaryVariant = 'neutral',
  primaryDanger,
  cancelText = 'Cancel',
  onCancel,
  onSubmit,
  primaryIsSubmit = false,
  footerAlign = 'end',
  hideFooter = false,
}: ModalProps) {
  const footer = hideFooter ? undefined : (
    <OverlayFooter
      primaryText={primaryText}
      onPrimary={onPrimary}
      isSubmit={primaryIsSubmit}
      primaryVariant={primaryVariant}
      primaryDanger={primaryDanger}
      loading={loading}
      cancelText={cancelText}
      onCancel={onCancel ?? onClose}
      align={footerAlign}
    />
  );

  return (
    <AntdModal
      open={open}
      onCancel={onClose}
      centered
      footer={null}
      closable={false}
      title={null}
      width={width ?? SIZE_WIDTH[size]}
      mask={{ closable: maskClosable }}
      keyboard={closeOnEsc}
      destroyOnHidden
      className={styles.modal}
      classNames={{ container: styles.containerReset }}
    >
      <OverlayChrome title={title} onClose={onClose} footer={footer} onSubmit={onSubmit}>
        {children}
      </OverlayChrome>
    </AntdModal>
  );
}
